import ConfigHelper from 'framework/helpers/config'
import { of, throwError } from 'rxjs'
import VoltError from 'VoltError'
import Fetch from '../fetch'
import DataHelper from 'framework/helpers/data'
import { mergeMap } from 'rxjs/operators'
import { userDeviceFactory } from '../factories'
import Constants from 'api-constants'
import { serialize } from 'helpers/index.js'

/**
 * Deal with Devices action (add/remove/get)
 */
export default class DeviceApi extends Fetch {
    _nagraApiUrl = ConfigHelper.getInstance().getConfig('urls').apiUrl

    /**
     * Get the account's device list containing
     * @param {Boolean} includeInactiveDevices Returns also inactive device
     * @returns {Observable<Array<BackendDevice>>}
     */
    getDeviceList({ includeInactiveDevices = false } = {}) {
        let offset = 0
        const limit = 100
        return this.recursiveFetch(
            (_, limit, offset) => {
                return this.fetch({
                    url: `${this._nagraApiUrl}/adm/v1/user/devices${serialize({
                        page: offset / limit + 1,
                        limit,
                    })}`,
                    method: 'GET',
                    log: 'GET_USER_DEVICES',
                    headers: {
                        'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        'Client-token': DataHelper.getInstance().getSecondaryAccessToken(),
                    },
                })
            },
            { offset, limit, responseResource: 'resourceSet' }
        ).pipe(mergeMap((devices = []) => of(userDeviceFactory(devices))))
    }

    // --------------------- FEATURE NOT SUPPORTED ---------------------
    /**
     * This method is called at the initialization to initialize the device service
     * In some backend it is the moment to create a device and initialize the list of the devices
     * @param {Object} deviceProperties
     * @param {Object} screenDimensions
     * @returns
     */
    initializeUserDevice({ deviceProperties, screenDimensions } = {}) {
        return of(true)
    }

    /**
     * This method verifies if the device exist in the backend
     * @param {String} deviceId Backend device ID
     * @returns {Observable<Boolean>} boolean indicating is the device exists or not
     */
    isExistingDevice({ deviceId } = {}) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[isExistingDevice] Feature not supported',
            })
        )
    }

    /**
     * Delete a device in the backend
     * @param {Object} data
     * @param {Object} [data.backendDeviceId]
     * @param {Object} data.backendDevicePublicId
     * @returns {Observable<Boolean>}
     */
    deleteDevice({ backendDeviceId, publicDeviceId } = {}) {
        return this.fetch({
            url: `${this._nagraApiUrl}/adm/v1/user/devices/${publicDeviceId}/deactivate`, // According to nagra device public id should be used for device operation
            method: 'PUT',
            log: 'DEACTIVATE DEVICE',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                'Client-token': DataHelper.getInstance().getSecondaryAccessToken(),
            },
        }).pipe(
            mergeMap(({ response: { modifiedCount = 0 } = {} } = {}) => of(Boolean(modifiedCount)))
        )
    }

    /**
     * Retrieve the device of the user
     * @returns {Observable<BackendDevice>}
     */
    getUserDevice() {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[getUserDevice] Feature not supported',
            })
        )
    }

    /**
     * Register a device in the backend
     * @param {Object} data
     * @param {Object} [data.screenDimensions]
     * @param {Object} [data.deviceName]
     * @returns {Observable<Boolean>}
     */
    addDevice({ screenDimensions, deviceName }) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[addDevice] Feature not supported',
            })
        )
    }
    /**
     * This method sends keepAlive request to the backend
     */
    keepAliveDevice() {
        return of(true)
    }

    /**
     * Modifies the user's device, specifically device name
     *
     * @param {object} args
     * @param {object} args.deviceNewName New device name for modification
     * @param {object} args.publicDeviceId The device id
     * @returns Observable
     */
    modifyDevice({ publicDeviceId, deviceNewName } = {}) {
        return this.fetch({
            url: `${this._nagraApiUrl}/adm/v1/user/devices/${publicDeviceId}/name`, // According to nagra device public id should be used for device operation
            method: 'PUT',
            log: '[RENAME DEVICE]',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                'Client-token': DataHelper.getInstance().getSecondaryAccessToken(),
            },
            body: {
                name: deviceNewName,
            },
        }).pipe(mergeMap(({ response } = {}) => of(response)))
    }
}
